import { PolymarketMergeHistory } from '@/components/PolymarketMergeHistory';
import { decodePolyMerges, type PolyMergeAccounting } from '@/lib/polymarket/mergeAccounting';
import { UsTraderSection, type UsTraderSummary } from './UsTraderSection';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ComponentProps } from 'react';
import { ArrowLeft, Crosshair, Loader2, RefreshCw, Settings as SettingsIcon } from 'lucide-react';
import { apiFetch, getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { useTickerFeedNeeds } from '@/hooks/useTickerFeedNeeds';
import { useMarketStore } from '@/stores/marketStore';
import { describeMarket } from '@/lib/kalshiTicker';
import { reconstructOrderTraderSide } from '@/lib/kalshiApi/orderReadV2';
import { computeHeadroom, formatHeadroomDisplay } from '@/lib/limits';
import { mirrorCents } from '@/lib/pricing/subCent';
import { effectiveOrgBalancesVisible, effectiveUsEnabled, type TraderRules } from '@/types/shared/admin';
import { fmtLastFillAt } from '@/components/cards/cardFormat';
import { buildPolyPositionVM } from '@/components/cards/positionCardModel';
import { cardTargetKey } from '@/lib/cardAnnotations/client';
import type { CardAnnotationTransport, CardTarget } from '@/lib/cardAnnotations/types';
import { kalshiOrderNoteTarget, polyOrderNoteTarget, polyPositionNoteTarget, useTraderNotes } from './traderNotes';
import { TraderNoteBadge, TraderNoteRow } from './TraderNoteBadge';
import { MarketTypeChip } from '@/components/cards/cardKit';
import { usePolyFeeSchedules } from '@/hooks/usePolyFeeSchedules';
import {
  describePolyMarketCached,
  describePolyOrderCached,
  polyPositionLiveData,
} from '@/lib/polymarket/describeOrder';
import {
  polyOpenOrderRemaining,
  type PolyRelayOpenOrder,
  type PolyRelayPosition,
} from '@/lib/polymarket/relay';
import { EditLimitsModal } from '../Manager/EditLimitsModal';
import {
  kalshiReportedBalanceTitle,
  readKalshiReportedBalanceCents,
} from '@/lib/kalshiBalance';
import {
  EMPTY_POLY_FILLS_PANEL,
  managerPolyOrdersReadable,
  managerPolyPositionsReadable,
  nextPolyFillsPanel,
  polyPriceCents,
  polyTimestamp,
  unwrapManagerPolyFills,
  unwrapManagerPolyOrders,
  type ManagerPolyPnl,
  type PolyFillsPanel,
} from './polyModel';
import { nextOrgPanel, orgPanelTileCount, unreadOrgPanel, type OrgReadPanel } from '../OrgDetail/orgReadPanel';
import { decodeKalshiRows, kalshiPanelCurrent, kalshiPanelUnread, kalshiReadNote } from './kalshiReads';

interface TraderDetailProps {
  traderId: string;
  username?: string;
  orgId?: string;
  onBack: () => void;
  /**
   * #976 R2: transport for the READ-ONLY view of this trader's notes. `TraderDetailRoute` in App.tsx passes
   * the browser singleton (#976 T5); without it (a test, a route that passes none) nothing is requested or rendered.
   */
  notesTransport?: CardAnnotationTransport;
}

interface RawPosition {
  ticker?: string;
  market_ticker?: string;
  netCount?: number;
  position?: number;
  quantity?: number;
  side?: 'yes' | 'no';
  avgEntryPriceCents?: number;
  lastFillAt?: string | null;
  average_price?: number;
  realizedPnlCents?: number;
}

interface RawOrder {
  order_id?: string;
  id?: string;
  ticker?: string;
  market_ticker?: string;
  side?: 'yes' | 'no';
  status?: string;
  type?: string;
  count?: number | string;
  count_fp?: number | string;
  quantity?: number | string;
  remaining_count?: number | string;
  remaining_count_fp?: number | string;
  remaining_quantity?: number | string;
  yes_price?: number | string;
  no_price?: number | string;
  yes_price_dollars?: number | string;
  no_price_dollars?: number | string;
  price?: number | string;
}

interface RawFill {
  fill_id?: string;
  trade_id?: string;
  ticker?: string;
  market_ticker?: string;
  side?: 'yes' | 'no';
  count?: number | string;
  count_fp?: number | string;
  fill_count_fp?: number | string;
  quantity?: number | string;
  yes_price?: number | string;
  no_price?: number | string;
  yes_price_dollars?: number | string;
  no_price_dollars?: number | string;
  price?: number | string;
  price_dollars?: number | string;
  created_time?: string;
  created_at?: string;
}

function pickNum(...c: unknown[]): number | undefined {
  for (const v of c) {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string' && v !== '') {
      const n = parseFloat(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return undefined;
}

interface FillPnl {
  unrealizedCents: number | null;
  realizedCents: number | null;
  /** A mark-based figure exists for this fill but is not shown (`openKeys` is null). */
  markWithheld?: boolean;
}

/**
 * Walks fills oldest→newest per (ticker, side), maintaining the same running
 * (netCount, costBasis) state the relay's positions service uses. For each
 * fill we attach:
 *   - unrealizedCents: only set on BUY fills, marks the contracts in this
 *     fill to current market price.
 *   - realizedCents: only set on SELL fills, P&L vs running avg cost at the
 *     moment of the sell.
 * Returned as a map keyed by the original (DESC) index so render order can
 * stay newest-first.
 *
 * `openKeys` is null when the open positions or the fills themselves are not
 * current (#1433), and then no mark-based figure is given. With positions
 * unknown, an empty set would be wrong: it says everything has closed, and turns
 * every open BUY into "realized". With fills kept, a sell newer than the kept
 * rows may have closed a holding they still show as held. Realized figures from
 * sells and settlements don't depend on it, and stay.
 */
export function annotateFillsWithPnl(
  fills: RawFill[],
  getMarketPrice: (ticker: string) => number | null,
  openKeys: ReadonlySet<string> | null,
  settlements?: Record<string, SettlementResult>,
  settlementValuesCents?: Record<string, number>,
): Map<number, FillPnl> {
  const indexed = fills.map((raw, idx) => ({ raw, idx }));
  indexed.sort((a, b) => {
    const ta = new Date(a.raw.created_time ?? a.raw.created_at ?? 0).getTime();
    const tb = new Date(b.raw.created_time ?? b.raw.created_at ?? 0).getTime();
    return ta - tb;
  });

  const stateByKey = new Map<string, { netCount: number; costBasisCents: number }>();
  const out = new Map<number, FillPnl>();
  const idxToKey = new Map<number, string>();

  for (const { raw, idx } of indexed) {
    const ticker = raw.ticker ?? raw.market_ticker ?? '';
    const side: 'yes' | 'no' = raw.side ?? 'yes';
    const action = ((raw as { action?: string }).action ?? 'buy') as 'buy' | 'sell';
    const count = pickNum(raw.count, raw.count_fp, raw.fill_count_fp, raw.quantity) ?? 0;
    const sideDollars = side === 'yes' ? raw.yes_price_dollars : raw.no_price_dollars;
    const sideCents = side === 'yes' ? raw.yes_price : raw.no_price;
    const price = dollarsToCents(sideDollars ?? raw.price_dollars, sideCents ?? raw.price) ?? 0;

    const key = `${ticker}|${side}`;
    const state = stateByKey.get(key) ?? { netCount: 0, costBasisCents: 0 };

    let unrealizedCents: number | null = null;
    let realizedCents: number | null = null;

    if (action === 'buy') {
      const winner = settlements?.[ticker];
      if (winner != null && count > 0) {
        // Market is settled — credit this BUY fill directly. Kalshi scalar
        // results use the venue's exact YES redemption (NO is its complement);
        // a true void still refunds this fill's basis.
        const yesSettlementValue = settlementValuesCents?.[ticker];
        const payout = yesSettlementValue != null
          ? side === 'yes' ? yesSettlementValue : 100 - yesSettlementValue
          : winner === 'void' ? price : winner === side ? 100 : 0;
        realizedCents = Math.round((payout - price) * count);
      } else {
        const yesPrice = getMarketPrice(ticker);
        if (yesPrice != null && count > 0) {
          // #1453: `price` is already this fill's own-side price and `current`
          // is the mark mirrored into the same side, so NO profit is
          // `current - price` exactly like YES. Flipping the sign here inverted
          // every NO mark (a NO bought at 38¢ with YES at 40¢ read −22¢).
          const current = side === 'yes' ? yesPrice : mirrorCents(yesPrice);
          unrealizedCents = (current - price) * count;
        }
      }
      state.netCount += count;
      state.costBasisCents += count * price;
    } else if (action === 'sell') {
      const closing = Math.min(state.netCount, count);
      if (closing > 0) {
        const avgCost = state.costBasisCents / state.netCount;
        // #1453: avgCost and price are both own-side; a NO sold above its
        // average cost is a gain for NO just as it is for YES.
        const perContract = price - avgCost;
        realizedCents = Math.round(perContract * closing);
        state.costBasisCents -= Math.round(avgCost * closing);
        state.netCount -= closing;
      }
    }

    stateByKey.set(key, state);
    idxToKey.set(idx, key);
    out.set(idx, { unrealizedCents, realizedCents });
  }

  if (!openKeys) {
    for (const [idx, pnl] of out) {
      if (pnl.unrealizedCents != null) out.set(idx, { ...pnl, unrealizedCents: null, markWithheld: true });
    }
    return out;
  }

  // Settled-position rewrite: a (ticker, side) with positive remaining net
  // that no longer appears in the live open-positions list closed via
  // settlement (Kalshi /markets `result`), not via a SELL fill. The first
  // pass stamped its BUYs as unrealized; promote those to realized so the
  // column reflects what actually happened. For partial-sell-then-settled
  // positions this approximates (uses BUY-time stamped value rather than
  // re-deriving against the post-sell average), which is close enough.
  for (const [key, state] of stateByKey) {
    if (state.netCount > 0 && !openKeys.has(key)) {
      for (const [idx, k] of idxToKey) {
        if (k !== key) continue;
        const pnl = out.get(idx);
        if (pnl?.unrealizedCents != null) {
          out.set(idx, { unrealizedCents: null, realizedCents: pnl.unrealizedCents });
        }
      }
    }
  }

  return out;
}

function dollarsToCents(dollars: unknown, fallbackCents?: unknown): number | null {
  // 0.01¢ grain preserved — PnL annotation must consume the exact fill price,
  // not a display-rounded one (sub-cent contract, 2026-07-17).
  if (typeof dollars === 'string' && dollars !== '') {
    const n = parseFloat(dollars);
    if (!Number.isNaN(n)) return Math.round(n * 10000) / 100;
  }
  if (typeof dollars === 'number') {
    return dollars <= 1.01
      ? Math.round(dollars * 10000) / 100
      : Math.round(dollars * 100) / 100;
  }
  if (typeof fallbackCents === 'number') return Math.round(fallbackCents * 100) / 100;
  if (typeof fallbackCents === 'string' && fallbackCents !== '') {
    const n = parseFloat(fallbackCents);
    if (!Number.isNaN(n)) return Math.round(n * 100) / 100;
  }
  return null;
}

type SettlementResult = 'yes' | 'no' | 'void';

interface PnlSummary {
  realizedPnlCents?: number;
  realizedFeesCents?: number | null;
  realizedPnlNetCents?: number | null;
  openCount?: number;
  openNotionalCents?: number;
  settlements?: Record<string, SettlementResult>;
  settlementValuesCents?: Record<string, number>;
}


function formatDollars(cents: number): string {
  const abs = Math.abs(cents);
  const sign = cents >= 0 ? '+' : '-';
  return `${sign}$${(abs / 100).toFixed(2)}`;
}

function toArr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

/** Shared by the incomplete-panel list and the retained-fills filter below.
 *  Those two must agree on the label or the banner contradicts itself —
 *  naming fills as blank while also claiming its rows are retained. */
const POLY_FILLS_PANEL_LABEL = 'fills';

interface TraderLimitsSnapshot {
  id: string;
  username: string;
  maxContracts: number | null;
  maxPositions: number | null;
  traderRules: TraderRules | null;
  openContracts: number;
  openPositions: number;
  intentContracts: number;
}

const subscribeAuthSession = (notify: () => void) => useAuthStore.subscribe(notify);

/** #1061/#1266: the route keeps this page mounted across trader and caller changes, so
 *  one inner instance per session+trader+org: an identity change drops every piece of
 *  the outgoing trader's state (tiles, limits editor, Polymarket panels) on
 *  the very next render, whatever its in-flight reads later answer. */
export function TraderDetail(props: TraderDetailProps) {
  const epoch = useSyncExternalStore(subscribeAuthSession, getAuthSessionEpoch);
  return <TraderDetailForIdentity key={`${epoch}|${props.traderId}|${props.orgId ?? ''}`} {...props} />;
}

function TraderDetailForIdentity({ traderId, username, orgId, onBack, notesTransport }: TraderDetailProps) {
  const orgQueryParam = orgId ? `?orgId=${orgId}` : '';
  const callerRole = useAuthStore((s) => s.user?.role);
  const canEditLimits = callerRole === 'manager' || callerRole === 'admin';

  const [balance, setBalance] = useState<Record<string, unknown> | null>(null);
  const [balanceIsOrgPlaceholder, setBalanceIsOrgPlaceholder] = useState(false);
  // #1433: never kept through a failed read. This panel gives up its rows for a spinner on
  // every Refresh, as Polymarket Positions does, so there is no continuity to keep (#437).
  // Keeping them would also need an answer for Unrealized: a kept quantity times a live
  // price is neither "last confirmed" nor current.
  const [positionsPanel, setPositionsPanel] = useState<OrgReadPanel<RawPosition>>(unreadOrgPanel);
  const positions = positionsPanel.rows;
  // #1433: kept through a failed Refresh, inside this identity-keyed instance (#1061).
  const [ordersPanel, setOrdersPanel] = useState<OrgReadPanel<RawOrder>>(unreadOrgPanel);
  const orders = ordersPanel.rows;
  const [fillsPanel, setFillsPanel] = useState<OrgReadPanel<RawFill>>(unreadOrgPanel);
  const fills = fillsPanel.rows;
  const [pnl, setPnl] = useState<PnlSummary | null>(null);
  const [polyPositions, setPolyPositions] = useState<PolyRelayPosition[]>([]);
  const [polyOrders, setPolyOrders] = useState<PolyRelayOpenOrder[]>([]);
  // The latest Polymarket reads answered with a readable body and, for positions, no merge or
  // split conflict. Stricter than the amber line's `.ok`: a malformed 2xx still reads as empty in
  // the panel and says nothing there (#1433 leaves those as they are), but its count is unknown.
  const [polyPositionsKnown, setPolyPositionsKnown] = useState(false);
  const [polyOrdersKnown, setPolyOrdersKnown] = useState(false);
  const [polyFillsPanel, setPolyFillsPanel] = useState<PolyFillsPanel>(EMPTY_POLY_FILLS_PANEL);
  const [polyMerges, setPolyMerges] = useState<PolyMergeAccounting[] | null>(null);
  const [polyMergesUnavailable, setPolyMergesUnavailable] = useState(false);
  const [polyPnl, setPolyPnl] = useState<ManagerPolyPnl | null>(null);
  const [polyIncompletePanels, setPolyIncompletePanels] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  // #1023: the Refresh button re-reads the US section too.
  const [usRefresh, setUsRefresh] = useState(0);
  const [usSummary, setUsSummary] = useState<UsTraderSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [limits, setLimits] = useState<TraderLimitsSnapshot | null>(null);
  // The trader being edited, snapshotted when Edit limits opens. The modal
  // resets its form whenever its `trader` prop changes identity, and a literal
  // built on every render of this page reset it on every re-render: edits made
  // in the dialog were silently dropped before Save (found driving #1205).
  const [editTarget, setEditTarget] = useState<ComponentProps<typeof EditLimitsModal>['trader']>(null);

  // Subscribe to hydra-driven price ticks. Re-renders when prices change so
  // the Current / P&L columns stay live without polling Kalshi.
  const marketVersion = useMarketStore((s) => s.version);
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);
  // Consumer feed filters (feed-filters-per-view): this page marks ANOTHER
  // user's book — positions and resting orders on both venues, none of them
  // in the viewer's own account stores — so it names them. Fills are history
  // (labels resolve from the floor); a fill's open leg is in positions.
  // Undeclared (null) until the first fetch lands.
  const detailFeedIds = useMemo(
    () => (loading ? null : [
      ...positions.map((p) => p.ticker),
      ...orders.map((o) => o.ticker),
      ...polyPositions.map((p) => p.ticker),
      ...polyOrders.map((o) => o.asset_id ?? o.market),
    ].filter((id): id is string => typeof id === 'string' && id !== '')),
    [loading, positions, orders, polyPositions, polyOrders],
  );
  useTickerFeedNeeds('trader-detail', detailFeedIds, { fold: 'view' });
  void marketVersion;

  // #976 R2: a manager or admin reads the notes this trader wrote on their cards (read-only).
  const noteTargets = useMemo<CardTarget[]>(() => {
    if (!notesTransport) return [];
    const out: CardTarget[] = [];
    for (const o of orders) { const t = kalshiOrderNoteTarget(o.order_id); if (t) out.push(t); }
    for (const o of polyOrders) { const t = polyOrderNoteTarget(o); if (t) out.push(t); }
    for (const p of polyPositions) out.push(polyPositionNoteTarget(p.ticker, p.side));
    // One entry per card identity: a repeated target would come back twice in the server's unavailable
    // list and the client refuses such a reply, which would hide every note on the page.
    return [...new Map(out.map((t) => [cardTargetKey(t), t])).values()];
  }, [notesTransport, orders, polyOrders, polyPositions]);
  const traderNotes = useTraderNotes({ transport: notesTransport, traderId, targets: noteTargets, ready: !loading });
  const [openNote, setOpenNote] = useState<string | null>(null);
  const noteBadge = (t: CardTarget | null) => {
    if (!t) return null;
    const key = cardTargetKey(t);
    return <TraderNoteBadge record={traderNotes.get(key)} open={openNote === key} onToggle={() => setOpenNote((cur) => (cur === key ? null : key))} />;
  };
  const noteRow = (t: CardTarget | null, colSpan: number) => {
    if (!t) return null;
    const key = cardTargetKey(t);
    const record = traderNotes.get(key);
    return openNote === key && record?.note ? <TraderNoteRow record={record} colSpan={colSpan} /> : null;
  };

  // Only the latest read of this trader may write rows, errors or loading;
  // a Refresh, a limits save or unmount makes every earlier reply stale.
  const readSeq = useRef(0);
  const fetchAll = useCallback(async () => {
    const seq = ++readSeq.current;
    setLoading(true);
    setError(null);
    const safeFetch = async (url: string) => {
      try {
        const res = await apiFetch(url);
        const data = await res.json().catch(() => null);
        return { ok: res.ok, data };
      } catch {
        return { ok: false, data: null };
      }
    };

    // The org-rollup fetch is shared across the Manager grid and TraderDetail
    // so we don't add a per-trader limits endpoint just for this tile — the
    // rollup already carries every member's caps + usage. Filter to this
    // trader's row client-side.
    const [
      balRes,
      posRes,
      ordRes,
      fillRes,
      pnlRes,
      rollupRes,
      polyPosRes,
      polyOrdRes,
      polyFillRes,
      polyPnlRes,
    ] = await Promise.all([
      safeFetch(`/api/manager/traders/${traderId}/balance${orgQueryParam}`),
      safeFetch(`/api/manager/traders/${traderId}/positions${orgQueryParam}`),
      safeFetch(`/api/manager/traders/${traderId}/orders${orgQueryParam}`),
      safeFetch(`/api/manager/traders/${traderId}/fills${orgQueryParam}`),
      safeFetch(`/api/manager/traders/${traderId}/pnl${orgQueryParam}`),
      safeFetch(`/api/manager/org/positions-by-trader${orgQueryParam}`),
      safeFetch(`/api/manager/traders/${traderId}/polymarket/positions${orgQueryParam}`),
      safeFetch(`/api/manager/traders/${traderId}/polymarket/orders${orgQueryParam}`),
      safeFetch(`/api/manager/traders/${traderId}/polymarket/fills${orgQueryParam}`),
      safeFetch(`/api/manager/traders/${traderId}/polymarket/pnl${orgQueryParam}`),
    ]);
    if (seq !== readSeq.current) return;

    if (rollupRes.ok && rollupRes.data) {
      const rows = (rollupRes.data as { rows?: Array<Record<string, unknown>> }).rows ?? [];
      const row = rows.find((r) => r.userId === traderId);
      if (row) {
        setLimits({
          id: traderId,
          username: username ?? (row.username as string) ?? 'trader',
          maxContracts: (row.maxContracts as number | null) ?? null,
          maxPositions: (row.maxPositions as number | null) ?? null,
          traderRules: (row.traderRules as TraderRules | null) ?? null,
          openContracts: (row.openCount as number) ?? 0,
          openPositions: (row.openPositionsCount as number) ?? 0,
          intentContracts: (row.intentContracts as number) ?? 0,
        });
      } else {
        setLimits(null);
      }
    }

    if (balRes.ok && balRes.data) {
      const balData = balRes.data as Record<string, unknown>;
      setBalanceIsOrgPlaceholder(balData.scope === 'org');
      setBalance(balData);
    } else {
      setBalance(null);
      setBalanceIsOrgPlaceholder(false);
    }

    // From a fresh panel, so a failed read keeps nothing (see the state's comment).
    setPositionsPanel(nextOrgPanel(
      unreadOrgPanel<RawPosition>(),
      decodeKalshiRows<RawPosition>(posRes.ok, posRes.data, ['market_positions', 'positions', 'results']),
    ));

    const ordersDecoded = decodeKalshiRows<RawOrder>(ordRes.ok, ordRes.data, ['orders', 'results']);
    setOrdersPanel((prev) => nextOrgPanel(prev, ordersDecoded));

    const fillsDecoded = decodeKalshiRows<RawFill>(fillRes.ok, fillRes.data, ['fills', 'results']);
    setFillsPanel((prev) => nextOrgPanel(prev, fillsDecoded));

    setPnl(pnlRes.ok ? (pnlRes.data as PnlSummary) : null);

    let polyPositionsConflict = false;
    if (polyPosRes.ok && polyPosRes.data) {
      const data = polyPosRes.data as { positions?: unknown };
      const next = toArr<PolyRelayPosition>(data.positions);
      polyPositionsConflict = next.some((p) => ((p.mergeConflictCount ?? 0) > 0 || (p.splitConflictCount ?? 0) > 0));
      setPolyPositions(polyPositionsConflict ? [] : next);
    } else {
      setPolyPositions([]);
    }
    setPolyPositionsKnown(polyPosRes.ok && !polyPositionsConflict && managerPolyPositionsReadable(polyPosRes.data));
    setPolyOrders(polyOrdRes.ok ? unwrapManagerPolyOrders(polyOrdRes.data) : []);
    setPolyOrdersKnown(polyOrdRes.ok && managerPolyOrdersReadable(polyOrdRes.data));
    // A 2xx carrying a malformed body is an unavailable read, not an empty
    // history — `null` here covers both that and an HTTP/transport failure, and
    // the panel keeps its last confirmed rows through either. The update is
    // functional because `fetchAll` is memoized: reading `polyFillsPanel`
    // directly would either close over a stale value or, once added to this
    // callback's deps, make the mount effect refetch in a loop — every read
    // stores a fresh object, so `fetchAll` would change identity each time.
    const polyFillsDecoded = polyFillRes.ok ? unwrapManagerPolyFills(polyFillRes.data) : null;
    setPolyFillsPanel((prev) => nextPolyFillsPanel(prev, polyFillsDecoded));
    const decodedMerges = polyFillRes.ok ? decodePolyMerges(polyFillRes.data) : null;
    setPolyMergesUnavailable(decodedMerges === null);
    if (decodedMerges !== null) setPolyMerges(decodedMerges);
    setPolyPnl(polyPnlRes.ok ? (polyPnlRes.data as ManagerPolyPnl) : null);
    setPolyIncompletePanels(
      [
        [polyPositionsConflict ? 'positions (merge accounting conflict)' : 'positions', polyPosRes.ok && !polyPositionsConflict],
        ['orders', polyOrdRes.ok],
        [POLY_FILLS_PANEL_LABEL, polyFillsDecoded !== null],
        ['P&L', polyPnlRes.ok],
      ]
        .filter((entry) => !entry[1])
        .map((entry) => String(entry[0])),
    );

    const anyOk = [
      balRes,
      posRes,
      ordRes,
      fillRes,
      pnlRes,
      polyPosRes,
      polyOrdRes,
      polyFillRes,
      polyPnlRes,
    ].some((r) => r.ok);
    if (!anyOk) setError('Could not load this trader’s activity.');
    setLoading(false);
  }, [traderId, orgQueryParam, username]);
  // One refresh for the Refresh button and the US sections' Retry (#1023).
  const refreshAll = () => { fetchAll(); setUsRefresh((n) => n + 1); };

  useEffect(() => {
    // The sequence itself (not a DOM node): unmount supersedes any read in flight.
    const reads = readSeq;
    fetchAll();
    return () => { reads.current++; };
  }, [fetchAll]);

  const kalshiRealizedNetCents = pnl?.realizedPnlNetCents ?? null;
  const balanceCents = balanceIsOrgPlaceholder || !balance
    ? null
    : readKalshiReportedBalanceCents(balance);
  const polyFeeConditionIds = useMemo(
    () => polyPositions.map((position) => position.ticker),
    [polyPositions],
  );
  const { schedules: polyFeeSchedules } = usePolyFeeSchedules(polyFeeConditionIds);
  const polyRealizedNetCents = polyPnl?.realizedPnlNetCents ?? null;
  const currentUsSummary = usSummary?.key === `${traderId}${orgQueryParam}` ? usSummary : null;
  const usRealizedNetCents = currentUsSummary?.netCents ?? null;
  const polyFills = polyFillsPanel.fills;
  const polyFillsRetained = polyFillsPanel.disclosure === 'retained';
  const blankPolyPanels = polyFillsRetained
    ? polyIncompletePanels.filter((panel) => panel !== POLY_FILLS_PANEL_LABEL)
    : polyIncompletePanels;
  // A Polymarket part is a number only when it is known or the amber line discloses its failed
  // read. Otherwise (no read landed yet, a malformed 2xx) nothing on the page would say the 0 isn't one.
  const polyPositionsPart = polyPositionsKnown || polyIncompletePanels.some((panel) => panel.startsWith('positions'))
    ? polyPositions.length : '—';
  const polyOrdersPart = polyOrdersKnown || polyIncompletePanels.includes('orders') ? polyOrders.length : '—';
  const positionsCurrent = kalshiPanelCurrent(positionsPanel);
  const ordersCurrent = kalshiPanelCurrent(ordersPanel);
  const positionsNote = kalshiReadNote(positionsPanel, 'positions');
  const ordersNote = kalshiReadNote(ordersPanel, 'open orders');
  const fillsNote = kalshiReadNote(fillsPanel, 'fills');
  // A mark-based figure on a fill needs both reads current (annotateFillsWithPnl).
  const fillMarksTrusted = positionsCurrent && kalshiPanelCurrent(fillsPanel);

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mx-auto max-w-7xl space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 rounded px-2 py-1 text-xs"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <Crosshair size={20} className="text-muted-foreground" />
            <h1 className="text-xl font-semibold">{username ?? 'Trader'}</h1>
          </div>
          <button
            onClick={refreshAll}
            disabled={loading}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1.5 transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {error && (
          <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-7">
          <div
            className="bg-card border-border rounded-lg border px-4 py-3"
            title={kalshiReportedBalanceTitle(balanceIsOrgPlaceholder ? 'org' : 'user')}
          >
            <div className="text-muted-foreground text-xs">Balance (Kalshi)</div>
            <div className="text-foreground mt-1 text-lg font-semibold">
              {balanceIsOrgPlaceholder
                ? <span className="text-muted-foreground text-xs font-normal">org-level</span>
                : balanceCents !== null
                  ? `$${(Number(balanceCents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                  : '—'}
            </div>
          </div>
          <div className="bg-card border-border rounded-lg border px-4 py-3">
            <div className="text-muted-foreground text-xs">Realized P&L (Kalshi, net)</div>
            <div
              className={`mt-1 text-lg font-semibold ${
                kalshiRealizedNetCents == null
                  ? 'text-muted-foreground'
                  : kalshiRealizedNetCents >= 0
                    ? 'text-green-400'
                    : 'text-red-400'
              }`}
            >
              {kalshiRealizedNetCents == null ? '—' : formatDollars(kalshiRealizedNetCents)}
            </div>
            {pnl && kalshiRealizedNetCents == null && (
              <div className="mt-0.5 text-[10px] text-amber-400">fee coverage incomplete</div>
            )}
          </div>
          <div className="bg-card border-border rounded-lg border px-4 py-3">
            <div className="text-muted-foreground text-xs">Realized P&L (Poly, net)</div>
            <div
              className={`mt-1 text-lg font-semibold ${
                polyRealizedNetCents == null
                  ? 'text-muted-foreground'
                  : polyRealizedNetCents >= 0
                    ? 'text-green-400'
                    : 'text-red-400'
              }`}
            >
              {polyRealizedNetCents == null ? '—' : formatDollars(polyRealizedNetCents)}
            </div>
            {polyPnl && polyRealizedNetCents == null && (
              <div className="mt-0.5 text-[10px] text-amber-400">fee coverage incomplete</div>
            )}
          </div>
          <div className="bg-card border-border rounded-lg border px-4 py-3" data-testid="trader-us-realized">
            <div className="text-muted-foreground text-xs">Realized P&amp;L (Poly US, net)</div>
            <div className={`mt-1 text-lg font-semibold ${usRealizedNetCents == null ? 'text-muted-foreground'
              : usRealizedNetCents >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {usRealizedNetCents == null ? '—' : formatDollars(usRealizedNetCents)}
            </div>
            <div className="text-muted-foreground mt-0.5 text-[10px]">
              {currentUsSummary?.accounts === 'absent' ? 'No US account' : usRealizedNetCents === null
                ? (currentUsSummary?.accounts === 'loading' || !currentUsSummary ? 'Loading' : 'Accounting unavailable') : 'Recorded history and fees'}
            </div>
          </div>
          <div className="bg-card border-border rounded-lg border px-4 py-3">
            <div className="text-muted-foreground text-xs">Open Positions</div>
            <div className="text-foreground mt-1 text-lg font-semibold">
              {!positionsCurrent || !polyPositionsKnown || currentUsSummary?.positionCount == null
                ? '—'
                : positions.length + polyPositions.length + currentUsSummary.positionCount}
            </div>
            <div className="text-muted-foreground mt-0.5 text-[10px]">
              {orgPanelTileCount(positionsPanel, positions.length)} Kalshi · {polyPositionsPart} Poly · {currentUsSummary?.positionCount ?? '—'} US
            </div>
          </div>
          <div className="bg-card border-border rounded-lg border px-4 py-3">
            <div className="text-muted-foreground text-xs">Open Orders (K+P)</div>
            <div className="text-foreground mt-1 text-lg font-semibold">
              {ordersCurrent && polyOrdersKnown ? orders.length + polyOrders.length : '—'}
            </div>
            <div className="text-muted-foreground mt-0.5 text-[10px]">
              {orgPanelTileCount(ordersPanel, orders.length)} Kalshi · {polyOrdersPart} Poly
            </div>
          </div>
          <GuardrailsTile
            limits={limits}
            canEdit={canEditLimits}
            onEdit={() => limits && setEditTarget({
              id: limits.id,
              username: limits.username,
              maxContracts: limits.maxContracts,
              maxPositions: limits.maxPositions,
              traderRules: limits.traderRules,
            })}
          />
        </div>

        {limits && (
          <EditLimitsModal
            isOpen={editTarget !== null}
            onClose={() => setEditTarget(null)}
            trader={editTarget}
            orgQueryParam={orgQueryParam}
            currentUsage={{
              openContracts: limits.openContracts,
              openPositions: limits.openPositions,
              intentContracts: limits.intentContracts,
            }}
            onConflict={() => { fetchAll(); }}
            onSaved={(next) => {
              setLimits((prev) => prev ? { ...prev, ...next } : prev);
              // Re-fetch the rollup so the bars reflect the new caps + rules.
              fetchAll();
            }}
          />
        )}

        {/* Positions */}
        <div className="bg-card border-border rounded-lg border">
          <div className="border-border border-b px-4 py-2.5">
            <h2 className="text-foreground text-sm font-medium">Kalshi Positions</h2>
          </div>
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
            </div>
          ) : positionsNote ? (
            <KalshiBlankNote note={positionsNote} />
          ) : positions.length === 0 ? (
            <p className="text-muted-foreground px-4 py-6 text-center text-sm">
              No open positions
            </p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Market</th>
                  <th className="px-3 py-2 text-center">Side</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Avg</th>
                  <th className="px-3 py-2 text-right">Last fill</th>
                  <th className="px-3 py-2 text-right">Current</th>
                  <th className="px-3 py-2 text-right">Unrealized (gross)</th>
                  <th className="px-3 py-2 text-right">Realized (gross)</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p, i) => {
                  const ticker = p.ticker ?? p.market_ticker ?? '—';
                  const isV2 = typeof p.netCount === 'number';
                  const side: 'yes' | 'no' = isV2
                    ? (p.side ?? 'yes')
                    : (p.position ?? 0) >= 0
                      ? 'yes'
                      : 'no';
                  const qty = isV2 ? (p.netCount ?? 0) : Math.abs(p.position ?? p.quantity ?? 0);
                  const avg = isV2 ? (p.avgEntryPriceCents ?? 0) : Number(p.average_price ?? 0);
                  const yesPrice = getMarketPrice(ticker);
                  const currentPrice =
                    yesPrice == null ? null : side === 'yes' ? yesPrice : 100 - yesPrice;
                  // Both avg and currentPrice are in side-native space (the
                  // price for the side actually held). One uniform formula.
                  const unrealized =
                    currentPrice == null ? null : (currentPrice - avg) * qty;
                  const realized = typeof p.realizedPnlCents === 'number' ? p.realizedPnlCents : null;
                  const { marketLabel, betTeam: fullBetTeam, badgeTeam, marketType } = describeMarket(ticker, side);
                  // #964: an NFL ladder prop's badge carries the player and rung (#603:
                  // one line); its stat stacks under it so the column stays badge-wide.
                  const betTeam = badgeTeam ?? fullBetTeam;
                  return (
                    <tr key={i} className="border-border/40 border-b" title={ticker}>
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        {marketLabel}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex flex-col items-center gap-0.5">
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                              side === 'yes'
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {betTeam ? `(${betTeam}) ` : ''}{String(side).toUpperCase()}
                          </span>
                          <MarketTypeChip marketType={marketType} />
                        </span>
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {qty}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                        {avg ? `${avg}¢` : '—'}
                      </td>
                      <td
                        className="text-muted-foreground px-3 py-2 text-right font-mono text-xs"
                        title={p.lastFillAt ?? undefined}
                      >
                        {fmtLastFillAt(p.lastFillAt)}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {currentPrice == null ? '—' : `${currentPrice}¢`}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs font-medium ${
                          unrealized == null
                            ? 'text-muted-foreground'
                            : unrealized >= 0
                              ? 'text-green-400'
                              : 'text-red-400'
                        }`}
                      >
                        {unrealized == null ? '—' : formatDollars(unrealized)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs font-medium ${
                          realized == null
                            ? 'text-muted-foreground'
                            : realized >= 0
                              ? 'text-green-400'
                              : 'text-red-400'
                        }`}
                      >
                        {realized == null ? '—' : formatDollars(realized)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Open Orders */}
        <div className="bg-card border-border rounded-lg border">
          <div className="border-border border-b px-4 py-2.5">
            <h2 className="text-foreground text-sm font-medium">Kalshi Open Orders</h2>
          </div>
          {ordersNote && orders.length > 0 && <KalshiKeptRowsNote note={ordersNote} />}
          {kalshiPanelUnread(ordersPanel) ? (
            <KalshiSectionSpinner />
          ) : ordersNote && orders.length === 0 ? (
            <KalshiBlankNote note={ordersNote} />
          ) : orders.length === 0 ? (
            <p className="text-muted-foreground px-4 py-6 text-center text-sm">No open orders</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Market</th>
                  <th className="px-3 py-2 text-center">Side</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Bid</th>
                  <th className="px-3 py-2 text-right">Price</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o, i) => {
                  const ticker = o.ticker ?? o.market_ticker ?? '—';
                  // V2 single-book: a resting buy-NO reads back side:'yes'/
                  // action:'sell'/outcome_side:'no'. Reconstruct the trader-native
                  // side so the manager sees a NO book as NO (the /orders endpoint
                  // returns raw orders; this table never ran parseOrders). The
                  // bid/price derivation below already complements the leg.
                  const { side } = reconstructOrderTraderSide(o);
                  const qty =
                    pickNum(o.remaining_count, o.remaining_count_fp, o.remaining_quantity, o.count, o.count_fp, o.quantity) ?? 0;
                  // Side-specific bid first; fall back to the opposite-side
                  // price flipped (Kalshi often only emits yes_price even for
                  // NO orders, so a 58c NO arrives as yes_price=42).
                  const sideDollars = side === 'yes' ? o.yes_price_dollars : o.no_price_dollars;
                  const sideCents = side === 'yes' ? o.yes_price : o.no_price;
                  const oppositeDollars = side === 'yes' ? o.no_price_dollars : o.yes_price_dollars;
                  const oppositeCents = side === 'yes' ? o.no_price : o.yes_price;
                  const sideBid = dollarsToCents(sideDollars, sideCents);
                  const oppositeBid = dollarsToCents(oppositeDollars, oppositeCents);
                  const bid =
                    sideBid != null && sideBid > 0
                      ? sideBid
                      : oppositeBid != null && oppositeBid > 0
                        ? 100 - oppositeBid
                        : dollarsToCents(undefined, o.price);
                  const yesPrice = getMarketPrice(ticker);
                  const currentPrice =
                    yesPrice == null ? null : side === 'yes' ? yesPrice : 100 - yesPrice;
                  const { marketLabel, betTeam: fullBetTeam, badgeTeam, marketType } = describeMarket(ticker, side);
                  // #964: an NFL ladder prop's badge carries the player and rung (#603:
                  // one line); its stat stacks under it so the column stays badge-wide.
                  const betTeam = badgeTeam ?? fullBetTeam;
                  const noteTarget = kalshiOrderNoteTarget(o.order_id);
                  return (
                    <Fragment key={o.order_id ?? o.id ?? i}>
                    <tr
                      className="border-border/40 border-b"
                      title={ticker}
                    >
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        {marketLabel}{noteBadge(noteTarget)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex flex-col items-center gap-0.5">
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                              side === 'yes'
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {betTeam ? `(${betTeam}) ` : ''}{String(side).toUpperCase()}
                          </span>
                          <MarketTypeChip marketType={marketType} />
                        </span>
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {qty}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                        {bid != null && bid > 0 ? `${bid}¢` : '—'}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {currentPrice == null ? '—' : `${currentPrice}¢`}
                      </td>
                    </tr>
                    {noteRow(noteTarget, 5)}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Recent fills */}
        <div className="bg-card border-border rounded-lg border">
          <div className="border-border border-b px-4 py-2.5">
            <h2 className="text-foreground text-sm font-medium">Recent Kalshi Fills</h2>
          </div>
          {fillsNote && fills.length > 0 && <KalshiKeptRowsNote note={fillsNote} />}
          {kalshiPanelUnread(fillsPanel) ? (
            <KalshiSectionSpinner />
          ) : fillsNote && fills.length === 0 ? (
            <KalshiBlankNote note={fillsNote} />
          ) : fills.length === 0 ? (
            <p className="text-muted-foreground px-4 py-6 text-center text-sm">No recent fills</p>
          ) : (() => {
            const openKeys = fillMarksTrusted ? new Set(
              positions.map((p) => {
                const ticker = p.ticker ?? p.market_ticker ?? '';
                const isV2 = typeof p.netCount === 'number';
                const side: 'yes' | 'no' = isV2
                  ? (p.side ?? 'yes')
                  : (p.position ?? 0) >= 0
                    ? 'yes'
                    : 'no';
                return `${ticker}|${side}`;
              }),
            ) : null;
            const fillPnl = annotateFillsWithPnl(
              fills,
              getMarketPrice,
              openKeys,
              pnl?.settlements,
              pnl?.settlementValuesCents,
            );
            const marksWithheld = fills.slice(0, 30).some((_, i) => fillPnl.get(i)?.markWithheld);
            return (
            <>
            {marksWithheld && <KalshiKeptRowsNote note="Some P&L on these fills shows — until Kalshi positions and fills both load." />}
            <table className="w-full">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Market</th>
                  <th className="px-3 py-2 text-center">Side</th>
                  <th className="px-3 py-2 text-right">Count</th>
                  <th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2 text-right">Unrealized (gross)</th>
                  <th className="px-3 py-2 text-right">Realized (gross)</th>
                  <th className="px-3 py-2 text-right">When</th>
                </tr>
              </thead>
              <tbody>
                {fills.slice(0, 30).map((f, i) => {
                  const ticker = f.ticker ?? f.market_ticker ?? '—';
                  const side: 'yes' | 'no' = f.side ?? 'yes';
                  const count = pickNum(f.count, f.count_fp, f.fill_count_fp, f.quantity) ?? 0;
                  const sideDollars = side === 'yes' ? f.yes_price_dollars : f.no_price_dollars;
                  const sideCents = side === 'yes' ? f.yes_price : f.no_price;
                  const price =
                    dollarsToCents(sideDollars ?? f.price_dollars, sideCents ?? f.price);
                  const when = f.created_time ?? f.created_at;
                  const { marketLabel, betTeam: fullBetTeam, badgeTeam, marketType } = describeMarket(ticker, side);
                  // #964: an NFL ladder prop's badge carries the player and rung (#603:
                  // one line); its stat stacks under it so the column stays badge-wide.
                  const betTeam = badgeTeam ?? fullBetTeam;
                  const pnl = fillPnl.get(i) ?? { unrealizedCents: null, realizedCents: null };
                  return (
                    <tr
                      key={f.fill_id ?? f.trade_id ?? i}
                      className="border-border/40 border-b"
                      title={ticker}
                    >
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        {marketLabel}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex flex-col items-center gap-0.5">
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                              side === 'yes'
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {betTeam ? `(${betTeam}) ` : ''}{String(side).toUpperCase()}
                          </span>
                          <MarketTypeChip marketType={marketType} />
                        </span>
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {count}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {price != null && price > 0 ? `${price}¢` : '—'}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs font-medium ${
                          pnl.unrealizedCents == null
                            ? 'text-muted-foreground'
                            : pnl.unrealizedCents >= 0
                              ? 'text-green-400'
                              : 'text-red-400'
                        }`}
                      >
                        {pnl.unrealizedCents == null ? '—' : formatDollars(pnl.unrealizedCents)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs font-medium ${
                          pnl.realizedCents == null
                            ? 'text-muted-foreground'
                            : pnl.realizedCents >= 0
                              ? 'text-green-400'
                              : 'text-red-400'
                        }`}
                      >
                        {pnl.realizedCents == null ? '—' : formatDollars(pnl.realizedCents)}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right text-[11px]">
                        {when ? new Date(when).toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </>
            );
          })()}
        </div>

        {/* Polymarket stays in its own accounting panels. CLOB order fields,
            condition-id marks, and fee completeness differ from Kalshi; a
            visually merged table would make those semantic gaps invisible. */}
        {/* Two distinct disclosures, because retention makes the original
            sentence false for the panel it applies to: "blank … are not
            confirmed zeroes" describes a BLANK panel, and a retained fills
            table is not blank. Retained fills therefore drop out of the blank
            list and get the #125 wording instead. */}
        {(blankPolyPanels.length > 0 || polyFillsRetained) && (
          <div className="space-y-0.5 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
            {blankPolyPanels.length > 0 && (
              <div>
                Polymarket {blankPolyPanels.join(', ')} did not load; blank counts and tables below
                are not confirmed zeroes.
              </div>
            )}
            {polyFillsRetained && (
              <div>Showing the last confirmed Polymarket fills (refresh delayed or failed).</div>
            )}
          </div>
        )}
        <div className="bg-card border-border rounded-lg border">
          <div className="border-border flex items-center justify-between border-b px-4 py-2.5">
            <h2 className="text-foreground text-sm font-medium">Polymarket Positions</h2>
            <span className="text-muted-foreground text-[11px]">fee-aware liquidation estimate</span>
          </div>
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
            </div>
          ) : polyPositions.length === 0 ? (
            <p className="text-muted-foreground px-4 py-6 text-center text-sm">
              No open Polymarket positions
            </p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Market</th>
                  <th className="px-3 py-2 text-center">Side</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Avg</th>
                  <th className="px-3 py-2 text-right">Last fill</th>
                  <th className="px-3 py-2 text-right">Mark</th>
                  <th className="px-3 py-2 text-right">Cash Out</th>
                  <th className="px-3 py-2 text-right">P&L</th>
                  <th className="px-3 py-2 text-right">Paid Fees</th>
                </tr>
              </thead>
              <tbody>
                {polyPositions.map((position) => {
                  const description = describePolyMarketCached(position.ticker);
                  const nowCents = polyPositionLiveData(position.ticker, position.side).nowCents;
                  const vm = buildPolyPositionVM(
                    position,
                    null,
                    nowCents,
                    polyFeeSchedules.get(position.ticker),
                  );
                  // Net P&L needs both sides of fee completeness: the live
                  // close schedule and the actual historical entry fees.
                  // buildPolyPositionVM remains compatible with old cached
                  // payloads by treating absent paid fees as zero; this
                  // accounting view must be stricter and withhold instead.
                  const pnlCents =
                    position.feesPaidCents == null || vm.pnlDollars == null
                      ? null
                      : vm.pnlDollars * 100;
                  const noteTarget = polyPositionNoteTarget(position.ticker, position.side);
                  return (
                    <Fragment key={`${position.ticker}:${position.side}`}>
                    <tr
                      className="border-border/40 border-b"
                      title={position.ticker}
                    >
                      <td className="text-foreground max-w-[360px] px-4 py-2 text-xs">
                        <div className="flex min-w-0 items-center">
                          <span className="min-w-0 truncate">{description?.matchup ?? vm.title}</span>
                          {noteBadge(noteTarget)}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            position.side === 'yes'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {position.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {position.netCount.toLocaleString()}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                        {position.avgEntryPriceCents.toFixed(2)}¢
                      </td>
                      <td
                        className="text-muted-foreground px-3 py-2 text-right font-mono text-xs"
                        title={position.lastFillAt ?? undefined}
                      >
                        {fmtLastFillAt(position.lastFillAt)}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {nowCents == null ? '—' : `${nowCents.toFixed(2)}¢`}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {vm.cashOutDollars == null ? '—' : `$${vm.cashOutDollars.toFixed(2)}`}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs font-medium ${
                          pnlCents == null
                            ? 'text-muted-foreground'
                            : pnlCents >= 0
                              ? 'text-green-400'
                              : 'text-red-400'
                        }`}
                      >
                        {pnlCents == null ? '—' : formatDollars(pnlCents)}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                        {position.feesPaidCents == null
                          ? '—'
                          : `$${(position.feesPaidCents / 100).toFixed(2)}`}
                      </td>
                    </tr>
                    {noteRow(noteTarget, 9)}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-card border-border rounded-lg border">
          <div className="border-border border-b px-4 py-2.5">
            <h2 className="text-foreground text-sm font-medium">Polymarket Open Orders</h2>
          </div>
          {polyOrders.length === 0 ? (
            <p className="text-muted-foreground px-4 py-6 text-center text-sm">
              No open Polymarket orders
            </p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Market</th>
                  <th className="px-3 py-2 text-center">Outcome</th>
                  <th className="px-3 py-2 text-center">Action</th>
                  <th className="px-3 py-2 text-right">Remaining</th>
                  <th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2 text-right">Placed</th>
                </tr>
              </thead>
              <tbody>
                {polyOrders.map((order, index) => {
                  const description = typeof order.asset_id === 'string'
                    ? describePolyOrderCached(order.asset_id)
                    : null;
                  const placed = polyTimestamp(order.timestamp);
                  const action = String(order.side ?? '').toUpperCase() || '—';
                  const priceCents = polyPriceCents(order.price);
                  const noteTarget = polyOrderNoteTarget(order);
                  return (
                    <Fragment key={order.id ?? order.orderID ?? order.orderId ?? index}>
                    <tr
                      className="border-border/40 border-b"
                      title={String(order.market ?? order.asset_id ?? '')}
                    >
                      <td className="text-foreground max-w-[360px] px-4 py-2 text-xs">
                        <div className="flex min-w-0 items-center">
                          <span className="min-w-0 truncate">{description?.matchup ?? String(order.market ?? order.asset_id ?? '—')}</span>
                          {noteBadge(noteTarget)}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center text-xs">
                        {description ? `(${description.betTeam}) ${description.side.toUpperCase()}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-center font-mono text-xs">{action}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {polyOpenOrderRemaining(order)?.toLocaleString() ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {priceCents == null ? '—' : `${priceCents}¢`}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right text-[11px]">
                        {placed ? placed.toLocaleString() : '—'}
                      </td>
                    </tr>
                    {noteRow(noteTarget, 6)}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-card border-border rounded-lg border">
          <div className="border-border border-b px-4 py-2.5">
            <h2 className="text-foreground text-sm font-medium">Recent Polymarket Fills</h2>
          </div>
          {polyFills.length === 0 ? (
            <p className="text-muted-foreground px-4 py-6 text-center text-sm">
              No recent Polymarket fills
            </p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Market</th>
                  <th className="px-3 py-2 text-center">Side</th>
                  <th className="px-3 py-2 text-center">Action</th>
                  <th className="px-3 py-2 text-right">Count</th>
                  <th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2 text-right">Actual Fee</th>
                  <th className="px-3 py-2 text-center">Liquidity</th>
                  <th className="px-3 py-2 text-right">When</th>
                </tr>
              </thead>
              <tbody>
                {polyFills.slice(0, 30).map((fill, index) => {
                  const description = describePolyMarketCached(fill.ticker);
                  return (
                    <tr
                      key={fill.fill_id ?? `${fill.order_id ?? 'fill'}:${index}`}
                      className="border-border/40 border-b"
                      title={fill.ticker}
                    >
                      <td className="text-foreground max-w-[360px] truncate px-4 py-2 text-xs">
                        {description?.matchup ?? fill.ticker}
                      </td>
                      <td className="px-3 py-2 text-center text-xs">{fill.side.toUpperCase()}</td>
                      <td className="px-3 py-2 text-center text-xs">{fill.action.toUpperCase()}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {fill.count.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {fill.price_cents.toFixed(2)}¢
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {fill.fees_cents == null ? '—' : `$${(fill.fees_cents / 100).toFixed(4)}`}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-center text-[11px]">
                        {fill.is_taker ? 'Taker' : 'Maker'}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right text-[11px]">
                        {polyTimestamp(fill.executed_at)?.toLocaleString() ?? '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <PolymarketMergeHistory rows={polyMerges ?? []} loading={polyMerges === null && !polyMergesUnavailable} unavailable={polyMergesUnavailable} retained={polyMerges !== null} />

        {/* #987 — Polymarket US orders and fills, same role authority as the
            panels above; shown only when the org has a US account. */}
        <UsTraderSection traderId={traderId} orgQueryParam={orgQueryParam} refreshKey={usRefresh} onRetry={refreshAll} onSummary={setUsSummary} />
      </div>
    </div>
  );
}

// ── Kalshi section states (#1433) ────────────────────────────────

function KalshiSectionSpinner() {
  return (
    <div className="flex justify-center py-6">
      <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
    </div>
  );
}

/** In place of the empty line. Amber text, not the amber box the Polymarket line uses: that box is one page-level notice. */
function KalshiBlankNote({ note }: { note: string }) {
  return <p role="status" className="text-amber-500 light:text-amber-700 px-4 py-6 text-center text-sm">{note}</p>;
}

/** Above the rows it describes, so it is read before a long list. */
function KalshiKeptRowsNote({ note }: { note: string }) {
  return <p role="status" className="text-amber-500 light:text-amber-700 border-border border-b px-4 py-2 text-xs">{note}</p>;
}

// ── Guardrails tile ──────────────────────────────────────────────

/** Tight rule summary for the compact tile, in shorter words than Manager
 *  LimitsRow's summarizeTraderRules and in its order: the venue and non-sport
 *  blocks lead and the sport list comes last, because the tile truncates the
 *  line and a long sport list must not push the blocks behind the hover (#1111). */
function summarizeRulesShort(rules: TraderRules | null): string | null {
  if (!rules) return null;
  const parts: string[] = [];
  if (!rules.polymarketEnabled) parts.push('no new Poly');
  if (!effectiveUsEnabled(rules)) parts.push('no new Poly US');
  if (!effectiveOrgBalancesVisible(rules)) parts.push('balances hidden');
  if (!rules.nonSportsEnabled) parts.push('no non-sport');
  if (rules.allowedSports !== null && rules.allowedSports.length > 0) {
    parts.push(rules.allowedSports.join('/') + ' only');
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

interface GuardrailsTileProps {
  limits: TraderLimitsSnapshot | null;
  canEdit: boolean;
  onEdit: () => void;
}

function GuardrailsTile({ limits, canEdit, onEdit }: GuardrailsTileProps) {
  if (!limits) {
    return (
      <div className="bg-card border-border rounded-lg border px-4 py-3">
        <div className="text-muted-foreground text-xs">Guardrails</div>
        <div className="text-muted-foreground mt-1 text-xs">—</div>
      </div>
    );
  }
  // Contracts cap includes resting buy intent so it mirrors what the relay
  // enforces on the next order placement.
  const contractsCurrent = limits.openContracts + limits.intentContracts;
  const cHead = computeHeadroom(contractsCurrent, limits.maxContracts);
  const pHead = computeHeadroom(limits.openPositions, limits.maxPositions);
  const statusColour: Record<string, string> = {
    green: 'text-emerald-400',
    yellow: 'text-amber-400',
    red: 'text-red-400',
    over: 'text-red-500',
    unlimited: 'text-blue-300',
  };
  const worst = [cHead.status, pHead.status].includes('over')
    ? 'over'
    : [cHead.status, pHead.status].includes('red')
      ? 'red'
      : [cHead.status, pHead.status].includes('yellow')
        ? 'yellow'
        : cHead.status === 'unlimited' && pHead.status === 'unlimited'
          ? 'unlimited'
          : 'green';

  return (
    <div className="bg-card border-border rounded-lg border px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="text-muted-foreground text-xs">Guardrails</div>
        {canEdit && (
          <button
            onClick={onEdit}
            title="Edit limits"
            className="text-muted-foreground hover:text-foreground rounded p-0.5"
          >
            <SettingsIcon size={11} />
          </button>
        )}
      </div>
      <div className={`mt-1 text-[11px] tabular-nums ${statusColour[worst]}`}>
        <div>Pos&nbsp;&nbsp; {formatHeadroomDisplay(limits.openPositions, limits.maxPositions)}</div>
        <div>Cont {formatHeadroomDisplay(contractsCurrent, limits.maxContracts)}</div>
        {summarizeRulesShort(limits.traderRules) && (
          <div
            className="mt-1 text-amber-400 truncate"
            title={summarizeRulesShort(limits.traderRules) ?? ''}
          >
            {summarizeRulesShort(limits.traderRules)}
          </div>
        )}
      </div>
    </div>
  );
}
